import React, { createContext, useState, useEffect } from 'react';

const MaterialSymbolsContext = createContext({
  isReady: false,
  isLoading: true
});

export const MaterialSymbolsProvider = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    // Test if font is truly loaded by rendering a test element and checking dimensions
    const isFontActuallyLoaded = () => {
      // Create a test element with a known icon
      const testEl = document.createElement('span');
      testEl.className = 'material-symbols-rounded';
      testEl.style.cssText = 'font-family: "Material Symbols Rounded"; font-size: 24px; position: absolute; visibility: hidden; white-space: nowrap;';
      testEl.textContent = 'search'; // A common icon name
      document.body.appendChild(testEl);
      
      // Get computed font family
      const computedFont = window.getComputedStyle(testEl).fontFamily;
      const width = testEl.offsetWidth;
      
      document.body.removeChild(testEl);
      
      // Check if font family includes Material Symbols and width is reasonable for icon font
      // Icon fonts typically render icons at similar width to height
      const hasMaterialSymbols = computedFont.toLowerCase().includes('material symbols');
      const hasReasonableWidth = width > 0 && width < 50; // Icon should be around 24px
      
      return hasMaterialSymbols && hasReasonableWidth;
    };

    // Initialize font loading
    const initFontLoading = async () => {
      try {
        // Wait for document.fonts to be ready first
        if (document.fonts) {
          await document.fonts.ready;
        }
        
        // Poll until font is actually rendered correctly (max 10 seconds)
        let attempts = 0;
        const maxAttempts = 100; // 100 * 100ms = 10 seconds
        
        while (attempts < maxAttempts && isMounted) {
          if (isFontActuallyLoaded()) {
            // Add small delay to ensure browser has finished rendering
            await new Promise(resolve => setTimeout(resolve, 150));
            
            if (isMounted) {
              console.log('Material Symbols font loaded successfully');
              setIsReady(true);
              setIsLoading(false);
            }
            return;
          }
          
          // Wait and try again
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        
        // If we get here, font didn't load in time but we proceed anyway
        console.warn('Material Symbols font may not have loaded correctly after timeout');
        if (isMounted) {
          setIsReady(true);
          setIsLoading(false);
        }
      } catch (e) {
        console.warn('Error loading Material Symbols font:', e);
        if (isMounted) {
          setIsReady(true);
          setIsLoading(false);
        }
      }
    };

    initFontLoading();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <MaterialSymbolsContext.Provider value={{ isReady, isLoading }}>
      {children}
    </MaterialSymbolsContext.Provider>
  );
};

export const useMaterialSymbols = () => {
  const context = React.useContext(MaterialSymbolsContext);
  if (!context) {
    throw new Error('useMaterialSymbols must be used within MaterialSymbolsProvider');
  }
  return context;
};
